// src/features/october/services/productService.js
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  query, 
  where, 
  orderBy, 
  limit 
} from 'firebase/firestore';
import { db } from '../../../services/firebase';

const COLLECTION_NAME = 'october_products';

export class ProductService {
  // Get all products
  static async getAllProducts() {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const products = [];
      
      querySnapshot.forEach((doc) => {
        products.push({ 
          id: doc.id, 
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate?.() || new Date()
        });
      });
      
      return products;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  }

  // Get product by ID
  static async getProductById(productId) {
    try {
      const docRef = doc(db, COLLECTION_NAME, productId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return { 
          id: docSnap.id, 
          ...data,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date()
        };
      } else {
        throw new Error('Product not found');
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      throw error;
    }
  }

  // Get products by category
  static async getProductsByCategory(category) {
    try {
      if (category === 'all' || category === 'perfumes') {
        return await this.getAllProducts();
      }
      
      const q = query(
        collection(db, COLLECTION_NAME),
        where('category', '==', category),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const products = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        products.push({ 
          id: doc.id, 
          ...data,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date()
        });
      });
      
      return products;
    } catch (error) {
      console.error('Error fetching products by category:', error);
      throw error;
    }
  }

  // Get products by gender/category filter
  static async getProductsByGender(gender) {
    try {
      if (gender === 'all') {
        return await this.getAllProducts();
      }
      
      const q = query(
        collection(db, COLLECTION_NAME),
        where('category', '==', gender),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const products = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        products.push({ 
          id: doc.id, 
          ...data,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date()
        });
      });
      
      return products;
    } catch (error) {
      console.error('Error fetching products by gender:', error);
      throw error;
    }
  }

  // Get featured products
  static async getFeaturedProducts(limitCount = 4) {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('featured', '==', true),
        where('inStock', '==', true),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      const querySnapshot = await getDocs(q);
      const products = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        products.push({ 
          id: doc.id, 
          ...data,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date()
        });
      });
      
      return products;
    } catch (error) {
      console.error('Error fetching featured products:', error);
      throw error;
    }
  }

  // Get in-stock products only
  static async getInStockProducts() {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('inStock', '==', true),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const products = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        products.push({ 
          id: doc.id, 
          ...data,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date()
        });
      });
      
      return products;
    } catch (error) {
      console.error('Error fetching in-stock products:', error);
      throw error;
    }
  }

  // Search products
  static async searchProducts(searchTerm) {
    try {
      // Note: Firestore doesn't support full-text search natively
      // This gets all products and filters client-side
      // For production, consider using Algolia or similar service
      const allProducts = await this.getAllProducts();
      
      if (!searchTerm || searchTerm.trim() === '') {
        return allProducts;
      }
      
      const term = searchTerm.toLowerCase().trim();
      
      return allProducts.filter(product =>
        product.name?.toLowerCase().includes(term) ||
        product.description?.toLowerCase().includes(term) ||
        product.type?.toLowerCase().includes(term) ||
        product.category?.toLowerCase().includes(term) ||
        product.notes?.top?.some(note => note.toLowerCase().includes(term)) ||
        product.notes?.heart?.some(note => note.toLowerCase().includes(term)) ||
        product.notes?.base?.some(note => note.toLowerCase().includes(term))
      );
    } catch (error) {
      console.error('Error searching products:', error);
      throw error;
    }
  }

  // Filter products (client-side filtering)
  static filterProducts(products, filters) {
    return products.filter(product => {
      // Category filter
      const categoryMatch = !filters.category || 
                           filters.category === 'all' || 
                           filters.category === 'perfumes' || 
                           product.category === filters.category;
      
      // Gender filter
      const genderMatch = !filters.gender || 
                         filters.gender === 'all' || 
                         product.category === filters.gender;
      
      // Price filter
      const priceMatch = !filters.maxPrice || 
                        product.priceValue <= filters.maxPrice;
      
      const priceMinMatch = !filters.minPrice || 
                           product.priceValue >= filters.minPrice;
      
      // Intensity filter
      const intensityMatch = !filters.intensity ||
                             filters.intensity === 'all' ||
                             product.intensity === filters.intensity;

      // Type filter
      const typeMatch = !filters.type ||
                        filters.type === 'all' ||
                        product.type === filters.type;
      
      // In stock filter
      const stockMatch = !filters.inStockOnly || 
                        product.inStock === true;
      
      // Featured filter
      const featuredMatch = !filters.featuredOnly || 
                           product.featured === true;
      
      return categoryMatch && 
             genderMatch && 
             priceMatch && 
             priceMinMatch && 
             intensityMatch && 
             typeMatch && 
             stockMatch && 
             featuredMatch;
    });
  }

  // Sort products
  static sortProducts(products, sortBy) {
    const sortedProducts = [...products];
    
    switch (sortBy) {
      case 'name-asc':
        return sortedProducts.sort((a, b) => a.name.localeCompare(b.name));
      case 'name-desc':
        return sortedProducts.sort((a, b) => b.name.localeCompare(a.name));
      case 'price-low':
        return sortedProducts.sort((a, b) => a.priceValue - b.priceValue);
      case 'price-high':
        return sortedProducts.sort((a, b) => b.priceValue - a.priceValue);
      case 'rating-high':
        return sortedProducts.sort((a, b) => b.rating - a.rating);
      case 'rating-low':
        return sortedProducts.sort((a, b) => a.rating - b.rating);
      case 'newest':
        return sortedProducts.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      case 'oldest':
        return sortedProducts.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      case 'featured':
        return sortedProducts.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
      default:
        return sortedProducts;
    }
  }

  // Get product statistics
  static async getProductStats() {
    try {
      const products = await this.getAllProducts();
      
      const stats = {
        total: products.length,
        inStock: products.filter(p => p.inStock).length,
        outOfStock: products.filter(p => !p.inStock).length,
        featured: products.filter(p => p.featured).length,
        categories: {},
        types: {},
        averagePrice: 0,
        priceRange: { min: 0, max: 0 },
        averageRating: 0
      };

      if (products.length === 0) {
        return stats;
      }

      let totalPrice = 0;
      let totalRating = 0;
      const prices = [];

      products.forEach(product => {
        // Category stats
        const category = product.category || 'uncategorized';
        stats.categories[category] = (stats.categories[category] || 0) + 1;
        
        // Type stats
        const type = product.type || 'uncategorized';
        stats.types[type] = (stats.types[type] || 0) + 1;
        
        // Price stats
        const price = product.priceValue || 0;
        totalPrice += price;
        prices.push(price);
        
        // Rating stats
        totalRating += product.rating || 0;
      });

      stats.averagePrice = totalPrice / products.length;
      stats.averageRating = totalRating / products.length;
      stats.priceRange.min = Math.min(...prices);
      stats.priceRange.max = Math.max(...prices);

      return stats;
    } catch (error) {
      console.error('Error getting product stats:', error);
      throw error;
    }
  }

  // Get related products (based on category and type)
  static async getRelatedProducts(productId, limitCount = 4) {
    try {
      const currentProduct = await this.getProductById(productId);
      
      const q = query(
        collection(db, COLLECTION_NAME),
        where('category', '==', currentProduct.category),
        where('inStock', '==', true),
        orderBy('rating', 'desc'),
        limit(limitCount + 1) // +1 to account for excluding current product
      );
      
      const querySnapshot = await getDocs(q);
      const products = [];
      
      querySnapshot.forEach((doc) => {
        if (doc.id !== productId) { // Exclude current product
          const data = doc.data();
          products.push({ 
            id: doc.id, 
            ...data,
            createdAt: data.createdAt?.toDate?.() || new Date(),
            updatedAt: data.updatedAt?.toDate?.() || new Date()
          });
        }
      });
      
      return products.slice(0, limitCount);
    } catch (error) {
      console.error('Error fetching related products:', error);
      throw error;
    }
  }
}